import React, { useEffect } from 'react';
import proptypes from 'prop-types';
const { func, arrayOf, shape, bool, string } = proptypes;
import {
  Dropdown,
  MenuItem,
  FormLabel,
  SkeletonBlock,
  SkeletonLine,
  SkeletonContent
} from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import {
  SORT_KEY_PROJECT_STATUS,
  SORT_KEY_PROJECT_NAME,
  SORT_KEY_LAST_MODIFIED,
  CARD_VIEW_TYPE
} from './project-summary-helper';
import { triggerAnalytics, sortAndFilterAnalytics } from '../helpers';

export const CardViewSortAndFilter = ({
  setSortCriteria,
  sortCriteria,
  onPageChange,
  isMobile,
  projects,
  loading,
  filterCriteria
}) => {

  const rawSortBy = sortCriteria?.length ? sortCriteria[0].sortBy : SORT_KEY_LAST_MODIFIED;
  const rawSortOrder = sortCriteria?.length ? sortCriteria[0].sortOrder : '';

  // Check if status filter is active
  const hasStatusFilter = filterCriteria?.projectStatus && filterCriteria.projectStatus.trim();

  // Derive the actual sort values based on filter state
  let sortBy;
  let sortOrder;

  if (hasStatusFilter && rawSortBy === SORT_KEY_PROJECT_STATUS) {
    // If status filter is active and we're sorting by status, force to Last Modified with no order
    sortBy = SORT_KEY_LAST_MODIFIED;
    sortOrder = '';
  } else {
    sortBy = rawSortBy;
    sortOrder = rawSortOrder;
  }

  // Reset sort criteria if currently sorting by Project Status when status filter becomes active
  useEffect(() => {
    if (hasStatusFilter && rawSortBy === SORT_KEY_PROJECT_STATUS) {
      setSortCriteria([]);
    }
  }, [hasStatusFilter, rawSortBy, setSortCriteria]);

  const sortOrderDictionary = {
    [SORT_KEY_PROJECT_NAME]: ['A to Z', 'Z to A'],
    [SORT_KEY_PROJECT_STATUS]: ['Active First', 'Archived First'],
    [SORT_KEY_LAST_MODIFIED]: ['', '']
  };

  const handleSortByChange = (event) => {
    if (event.target.value === SORT_KEY_LAST_MODIFIED) {
      setSortCriteria([]);
    } else {
      setSortCriteria([{
        sortBy: event.target.value,
        sortOrder: 'ASC'
      }]);
    }
    onPageChange(0);
  };

  const handleSortOrderChange = (event) => {
    setSortCriteria((prev) => {
      return [{
        sortBy: prev[0].sortBy,
        sortOrder: event.target.value
      }];
    });
    onPageChange(0);
  };

  useEffect(() => {
    if (sortCriteria.length || filterCriteria) {
      sortAndFilterAnalytics({
        sortCriteria,
        filterCriteria,
        viewType: CARD_VIEW_TYPE
      });
    }
  }, [sortCriteria, filterCriteria]);

  if (!projects?.length && loading && isMobile) {
    return (
      <div
        data-testid="projects-sort-placeholder"
        className="sui-grid sui-grid-cols-2 sui-gap-2 sui-pb-4"
      >
        <div className="sui-col-start-1 sui-mr-4">
          <SkeletonContent disablePadding initialSize="1/4">
            <SkeletonLine height={2} variant="single" />
            <SkeletonBlock height={8} aspect="wide" />
          </SkeletonContent>
        </div>
        <div className="sui-col-start-2">
          <SkeletonContent disablePadding initialSize="1/4">
            <SkeletonLine height={2} variant="single" />
            <SkeletonBlock height={8} aspect="wide" />
          </SkeletonContent>
        </div>
      </div>
    );
  }

  const sortByDropdownClass = classNames({
    'sui-w-56': !isMobile,
    'sui-mr-6': !isMobile,
    'sui-w-full': isMobile,
    'sui-mb-4': isMobile
  });

  const sortOrderDropdownClass = classNames({
    'sui-w-72': !isMobile,
    'sui-w-full': isMobile,
    'sui-mb-4': isMobile
  });

  return (
    <div className={`${!isMobile && 'sui-flex'}`}>
      <div className={sortByDropdownClass}>
        <FormLabel htmlFor="sort-by" id="sort-by-label">Sort By</FormLabel>
        <Dropdown value={sortBy} onChange={handleSortByChange} id="sort-by">
          <MenuItem value={SORT_KEY_PROJECT_NAME}>Project Name</MenuItem>
          {!hasStatusFilter && (
            <MenuItem value={SORT_KEY_PROJECT_STATUS}>Project Status</MenuItem>
          )}
          <MenuItem value={SORT_KEY_LAST_MODIFIED}>Last Modified</MenuItem>
        </Dropdown>
      </div>
      <div className={sortOrderDropdownClass}>
        <FormLabel htmlFor="sort-order" id="sort-order-label">Order</FormLabel>
        <Dropdown
          key={hasStatusFilter && rawSortBy === SORT_KEY_PROJECT_STATUS ? 'sort-order-reset' : 'sort-order-normal'}
          value={sortOrder}
          onChange={handleSortOrderChange}
          disabled={!sortOrder || sortBy === SORT_KEY_LAST_MODIFIED}
          id="sort-order"
        >
          <MenuItem value="ASC">{sortOrderDictionary[sortBy] && sortOrderDictionary[sortBy][0]}</MenuItem>
          <MenuItem value="DSC">{sortOrderDictionary[sortBy] && sortOrderDictionary[sortBy][1]}</MenuItem>
        </Dropdown>
      </div>
    </div>
  );
};

CardViewSortAndFilter.propTypes = {
  setSortCriteria: func,
  sortCriteria: arrayOf(shape({})),
  onPageChange: func,
  isMobile: bool,
  projects: arrayOf(shape({})),
  loading: bool,
  filterCriteria: shape({
    projectName: string,
    projectNumber: string,
    projectStatus: string
  })
};

CardViewSortAndFilter.defaultProps = {
  setSortCriteria: () => {},
  sortCriteria: [],
  onPageChange: () => {},
  isMobile: false,
  projects: [],
  loading: false,
  filterCriteria: {
    projectName: '',
    projectNumber: '',
    projectStatus: ''
  }
};